import { ConfigurationService } from 'src/app/services/modules/instant-rate-manager/configuration.service';
import {
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import {
  AppliedTermsService,
  getCombinedCategorySections,
  getIndependentCategorySections,
  TermsSection,
} from 'src/app/services/modules/instant-rate-manager/applied-terms.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { parseBoolean } from 'src/app/shared/utils/data.utils';

import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

/** Minimal shape of a configuration field returned by the API */
interface ConfigField {
  id: number;
  attribute_code: string;
  attribute_label?: string;
  display_type: string;
  value: any;
  value_pattern?: any;
  is_required?: boolean;
  permission_level?: string;
  group?: { name?: string; position?: number };
  group_name_val?: string;
  dynamicRangeKeys?: string[];
  dynamicKeys?: string[];
  hasEnabledToggle?: boolean;
}

@Component({
  selector: 'app-manage-configurations',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbTooltipModule],
  templateUrl: './manage-configurations.component.html',
  styleUrl: './manage-configurations.component.scss',
})
export class ManageConfigurationsComponent implements OnInit {
  @ViewChild('quoteTermsModalTemplate')
  quoteTermsModalTemplate!: TemplateRef<any>;
  @ViewChild('independentTermModalTemplate')
  independentTermModalTemplate!: TemplateRef<any>;

  private fb = inject(FormBuilder);
  private configurationService = inject(ConfigurationService);
  private rateManagerService = inject(RateManagerService);
  private appliedTermsService = inject(AppliedTermsService);
  private modalService = inject(NgbModal);
  private cdr = inject(ChangeDetectorRef);

  /**
   * STATE
   */
  categoryTypeArray: Array<any> = [];
  selectedCategory: any = null;
  isCategorySelected = false;
  isLoading = false;
  isSubmitting = false;
  isTogglingInstantQuotes = false;
  isReadOnlyUser = false; // TODO: Connect to permission service if needed
  formSubmitAttempted = false;

  instantQuotesEnabled = false;
  instantQuotesField: ConfigField | null = null;

  configForm!: FormGroup;
  dynamicFields: any[] = [];
  groupedFields: { [key: string]: any[] } = {};
  fieldTypes: string[] = [];

  isCategoryTermsLoading = false;
  viewGlobalSections: TermsSection[] = [];
  viewCategorySections: TermsSection[] = [];
  independentCategorySections: TermsSection[] = [];
  selectedIndependentSection: TermsSection | null = null;

  ngOnInit() {
    this.configForm = this.fb.group({});
    this.fetchCategories();
  }

  /**
   * DATA FETCHING
   */
  async fetchCategories() {
    this.isLoading = true;
    try {
      const response = await this.rateManagerService.getProviderRatesCategories(
        {},
        true,
      );
      this.categoryTypeArray = response.data.map((item: any) => ({
        value: item.id,
        label: item.name
          ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
          : item.name,
        description:
          item.description || `Manage settings for ${item.name} freight.`,
      }));
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      AlertUtil.error('Failed to load categories.');
    } finally {
      this.isLoading = false;
    }
  }

  async onSelectCategory(cat: any) {
    this.isCategorySelected = false;
    this.selectedCategory = cat;
    this.formSubmitAttempted = false;
    this.isLoading = true;

    // Reset instant quotes state before loading new category
    this.instantQuotesEnabled = false;
    this.instantQuotesField = null;

    try {
      const [configResponse] = await Promise.all([
        this.configurationService.getConfigurationsByCategoryId(cat.value),
        this.loadCategoryTerms(String(cat.value)),
      ]);
      this.dynamicFields = Array.isArray(configResponse)
        ? configResponse
        : configResponse.data || [];

      // Extract instant_quotes field — handled separately via dedicated toggle
      this.instantQuotesField =
        this.dynamicFields.find(
          (f: any) => f.attribute_code === 'instant_quotes',
        ) || null;
      if (this.instantQuotesField) {
        const val = this.instantQuotesField.value;
        this.instantQuotesEnabled = val === 'true' || val === true || val === 1;
        this.dynamicFields = this.dynamicFields.filter(
          (f: any) => f.attribute_code !== 'instant_quotes',
        );
      }

      const group: any = {};

      this.groupedFields = {};
      this.dynamicFields.forEach((field) => {
        const groupKey =
          field.group?.name || field.group_name_val || 'Other Settings';
        if (!this.groupedFields[groupKey]) this.groupedFields[groupKey] = [];
        this.groupedFields[groupKey].push(field);

        const isObjectField =
          field.display_type === 'json' ||
          field.display_type === 'checkbox_text_input' ||
          field.display_type === 'pricing_addon';
        const isRangeList =
          field.display_type === 'weight_range_list' ||
          field.display_type === 'surcharge_price';
        const isActionButton = field.display_type === 'button';

        if (isActionButton) {
          return;
        } else if (isRangeList) {
          let ranges = Array.isArray(field.value) ? field.value : [];

          // Ensure at least one default range exists if empty
          if (ranges.length === 0) {
            const defaultRange: any = { min: 0, max: 1000 };
            if (field.display_type === 'surcharge_price')
              defaultRange.fee = null;
            else if (field.display_type === 'weight_range_list')
              defaultRange.label = 'Range 1';
            ranges = [defaultRange];
          }

          // Discover all unique keys across all items, excluding 'min' and 'max'
          const dynamicRangeKeys = new Set<string>();
          ranges.forEach((r: any) => {
            Object.keys(r).forEach((k: string) => {
              if (k !== 'min' && k !== 'max') dynamicRangeKeys.add(k);
            });
          });

          // Ensure 'fee' exists for surcharge_price if not discovered
          if (
            field.display_type === 'surcharge_price' &&
            !dynamicRangeKeys.has('fee')
          ) {
            dynamicRangeKeys.add('fee');
          }

          field.dynamicRangeKeys = Array.from(dynamicRangeKeys);

          group[field.attribute_code] = this.fb.array(
            ranges.map((r: any, index: number) => {
              const rangeGroup: any = {
                min: [
                  index === 0 ? 0 : r.min || 0,
                  [
                    Validators.min(0),
                    Validators.required,
                    this.oneDecimalValidator,
                  ],
                ],
                max: [
                  r.max || 0,
                  [
                    Validators.min(0),
                    this.nonZeroRequiredValidator,
                    this.oneDecimalValidator,
                  ],
                ],
              };
              field.dynamicRangeKeys.forEach((k: string) => {
                const isFee =
                  k === 'fee' && field.display_type === 'surcharge_price';
                let defaultVal = r[k] ?? (isFee ? 0 : null);
                const validators = [];
                if (isFee) {
                  validators.push(Validators.required, Validators.min(0));
                }
                rangeGroup[k] = [defaultVal, validators];
              });
              return this.fb.group(rangeGroup, {
                validators: this.maxGreaterThanMinValidator(),
              });
            }),
            { validators: this.gaplessRangeValidator() },
          );
        } else if (isObjectField) {
          const objValue = field.value ||
            field.value_pattern || { enabled: false };
          const objGroup: any = {};

          // Identify keys excluding 'enabled' for dynamic rendering
          field.hasEnabledToggle = Object.keys(objValue).includes('enabled');
          const valueKeys = Object.keys(objValue).filter(
            (k) => k !== 'enabled',
          );
          if (field.attribute_code === 'tailgate_thresholds') {
            const tailgateKeyOrder = [
              'reject_max_weight_kg',
              'reject_max_length_m',
              'reject_max_width_m',
              'reject_max_height_m',
              'auto_trigger_weight_kg',
              'residential_service_auto_trigger_length_m',
            ];
            field.dynamicKeys = tailgateKeyOrder.filter((k) =>
              valueKeys.includes(k),
            );
          } else {
            field.dynamicKeys = valueKeys;
          }

          Object.keys(objValue).forEach((key) => {
            const val = objValue[key];
            const validators = [];
            if (key !== 'enabled' && typeof val === 'number') {
              validators.push(Validators.min(0));
            }
            objGroup[key] = [
              val ??
                (key === 'enabled' ? false : typeof val === 'number' ? 0 : ''),
              validators,
            ];
          });

          group[field.attribute_code] = this.fb.group(objGroup);
        } else {
          let initialValue = field.value;
          if (field.display_type === 'boolean') {
            initialValue = parseBoolean(initialValue);
          } else if (field.display_type === 'number' || field.display_type === 'decimal') {
            initialValue = initialValue !== null ? Number(initialValue) : null;
          }

          const validators = [];
          if (field.is_required) validators.push(Validators.required);
          if (
            field.display_type === 'number' ||
            field.display_type === 'decimal'
          )
            validators.push(Validators.min(0));

          group[field.attribute_code] = new FormControl(
            initialValue ?? '',
            validators,
          );
        }
      });

      this.fieldTypes = Object.keys(this.groupedFields).sort((a, b) => {
        const groupA = this.groupedFields[a][0].group?.position ?? 999;
        const groupB = this.groupedFields[b][0].group?.position ?? 999;
        return groupA - groupB;
      });
      this.configForm = this.fb.group(group);
      this.isCategorySelected = true;

      // Disable form if instant quotes is off
      if (this.instantQuotesField && !this.instantQuotesEnabled) {
        this.configForm.disable();
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
      AlertUtil.error('Could not load specific settings.');
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * ACTIONS
   */
  async saveConfiguration() {
    if (this.configForm.invalid) {
      this.formSubmitAttempted = true;
      this.configForm.markAllAsTouched();
      this.cdr.detectChanges();

      // Construct a specific error message
      let errorMessage = 'Please fill in all required fields.';
      const controls = this.configForm.controls;

      // Check for specific global/group errors
      let foundSpecificError = false;
      Object.keys(controls).forEach((key) => {
        if (foundSpecificError) return;

        const control = controls[key];
        const field = this.dynamicFields.find((f) => f.attribute_code === key);
        const fieldLabel = field?.attribute_label || 'Range';

        if (control.invalid) {
          if (control.hasError('firstRangeZeroError')) {
            errorMessage = `Configuration Error: First ${fieldLabel} must start at 0.`;
            foundSpecificError = true;
          } else if (control.hasError('gapError')) {
            errorMessage = `Configuration Error: ${fieldLabel}s must be discrete and continuous (e.g., 0-50, 51-100 or 0-50, 50.01-100).`;
            foundSpecificError = true;
          }
        }
      });

      // Log invalid fields (keeping existing logging logic)
      const invalidFields: string[] = [];
      Object.keys(this.configForm.controls).forEach((key) => {
        const control = this.configForm.get(key);
        if (control?.invalid) {
          invalidFields.push(key);
          console.log(`Invalid Field: ${key}`, control.errors);

          if (control instanceof FormArray) {
            control.controls.forEach((group, index) => {
              if (group.invalid) {
                console.log(
                  `Invalid Range at index ${index}`,
                  group.errors,
                  (group as FormGroup).controls['min']?.errors,
                  (group as FormGroup).controls['max']?.errors,
                );
              }
            });
          }
        }
      });
      console.warn('Form is invalid. Invalid fields:', invalidFields);

      AlertUtil.error(errorMessage);

      // Scroll to first error
      // Expanded selector to catch container alerts for range errors
      setTimeout(() => {
        const firstInvalidControl = document.querySelector(
          '.range-card--invalid, .config-input--invalid, .is-invalid, .ng-invalid.ng-touched:not(form)',
        );
        if (firstInvalidControl) {
          firstInvalidControl.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          const input =
            firstInvalidControl instanceof HTMLInputElement
              ? firstInvalidControl
              : firstInvalidControl.querySelector('input');
          input?.focus();
        }
      }, 100);

      return;
    }

    this.isSubmitting = true;

    const settingsPayload = this.dynamicFields
      .filter((field) => field.permission_level !== 'read_only')
      .map((field) => {
        let value = this.configForm.get(field.attribute_code)?.value;

        if (
          (field.display_type === 'number' ||
            field.display_type === 'decimal') &&
          value !== null
        ) {
          value = Number(value);
        }

        // Auto-generate labels for weight ranges and ensure numeric values
        if (
          (field.display_type === 'weight_range_list' ||
            field.display_type === 'surcharge_price') &&
          Array.isArray(value)
        ) {
          value = value.map((range: any, index: number) => {
            const mapped: any = { ...range };
            Object.keys(mapped).forEach((k: string) => {
              if (
                typeof mapped[k] === 'string' &&
                !isNaN(Number(mapped[k])) &&
                mapped[k] !== ''
              ) {
                mapped[k] = Number(mapped[k]);
              }
            });
            if (!mapped.label && !mapped.fee && !mapped.rate) {
              mapped.label = `Range ${index + 1}`;
            }
            return mapped;
          });
        }

        // Ensure numeric fields in JSON/checkbox_text_input objects are sent as numbers
        if (
          (field.display_type === 'json' ||
            field.display_type === 'checkbox_text_input' ||
            field.display_type === 'pricing_addon') &&
          value &&
          typeof value === 'object'
        ) {
          const coerced = { ...value };
          for (const key of Object.keys(coerced)) {
            if (key === 'enabled') continue;
            if (
              this.isNumber(field, key) &&
              coerced[key] !== null &&
              coerced[key] !== ''
            ) {
              coerced[key] = Number(coerced[key]);
            }
          }
          value = coerced;
        }

        return {
          setting_definition_id: field.id,
          setting_value: value,
        };
      });

    if (this.instantQuotesField) {
      settingsPayload.push({
        setting_definition_id: this.instantQuotesField.id,
        setting_value: this.instantQuotesEnabled,
      });
    }

    const payload = {
      category_id: this.selectedCategory.value,
      is_toggle: false,
      settings: settingsPayload,
    };

    try {
      const response =
        await this.configurationService.saveProviderCategorySettings(payload);
      this.formSubmitAttempted = false;
      AlertUtil.success(
        response?.message ||
          `${this.selectedCategory.label} settings updated successfully.`,
      );
    } catch (error: any) {
      console.error('Error saving settings:', error);
      AlertUtil.error(
        error?.error?.message || 'Failed to update configuration settings.',
      );
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * INSTANT QUOTES TOGGLE
   */
  async onToggleInstantQuotes() {
    if (!this.instantQuotesField || !this.selectedCategory) return;

    this.isTogglingInstantQuotes = true;
    const newValue = !this.instantQuotesEnabled;

    const payload = {
      category_id: this.selectedCategory.value,
      is_toggle: true,
      settings: [
        {
          setting_definition_id: this.instantQuotesField.id,
          setting_value: newValue,
        },
      ],
    };

    try {
      const response =
        await this.configurationService.saveProviderCategorySettings(payload);
      this.instantQuotesEnabled = newValue;

      if (!newValue) {
        this.configForm.disable();
      } else {
        this.configForm.enable();
        // Re-disable any read-only fields that should not be editable
        this.disableReadOnlyFields();
      }

      AlertUtil.success(
        response?.message ||
          `Instant quotes ${newValue ? 'enabled' : 'disabled'} successfully.`,
      );
    } catch (error: any) {
      console.error('Error toggling instant quotes:', error);
      AlertUtil.error(
        error?.error?.message || 'Failed to update instant quotes setting.',
      );
    } finally {
      this.isTogglingInstantQuotes = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * UI HELPERS
   */

  /** Re-disable form controls for fields marked as read-only by the API */
  private disableReadOnlyFields() {
    this.dynamicFields
      .filter((field) => field.permission_level === 'read_only')
      .forEach((field) => {
        const control = this.configForm.get(field.attribute_code);
        if (control) {
          control.disable();
        }
      });
  }

  getWeightRanges(code: string) {
    return this.configForm.get(code) as FormArray;
  }

  getRangeErrorCount(code: string): number {
    const array = this.getWeightRanges(code);
    if (!array) return 0;
    return array.controls.filter((r) => r.invalid).length;
  }

  addWeightRange(field: any) {
    const isSurcharge = field.display_type === 'surcharge_price';
    const rangeArray = this.configForm.get(field.attribute_code) as FormArray;
    const lastRange = rangeArray.at(rangeArray.length - 1)?.value;
    const newMin = lastRange
      ? parseFloat((Number(lastRange.max) + 1).toFixed(2))
      : 0;

    const rangeGroup: any = {
      min: [
        newMin,
        [Validators.min(0), Validators.required, this.oneDecimalValidator],
      ],
      max: [
        parseFloat((newMin + 10).toFixed(2)),
        [
          Validators.min(0),
          this.nonZeroRequiredValidator,
          this.oneDecimalValidator,
        ],
      ],
    };

    (field.dynamicRangeKeys || []).forEach((k: string) => {
      if (k === 'label') {
        rangeGroup[k] = [`Range ${rangeArray.length + 1}`];
      } else if (k === 'fee' && isSurcharge) {
        rangeGroup[k] = [0, [Validators.required, Validators.min(0)]];
      } else {
        rangeGroup[k] = [null];
      }
    });

    rangeArray.push(
      this.fb.group(rangeGroup, {
        validators: this.maxGreaterThanMinValidator(),
      }),
    );
  }

  removeWeightRange(code: string, index: number) {
    const array = this.getWeightRanges(code);
    if (array && array.length > 0) {
      array.removeAt(index);
    }
  }

  // NON-ZERO REQUIRED VALIDATOR — treats 0 the same as null/empty for required numeric fields
  nonZeroRequiredValidator = (
    control: AbstractControl,
  ): { [key: string]: any } | null => {
    const val = control.value;
    if (val === null || val === undefined || val === '' || Number(val) === 0) {
      return { required: true };
    }
    return null;
  };

  // TWO DECIMAL VALIDATOR (allows up to 2 decimal places, e.g. 0.01 steps)
  oneDecimalValidator = (
    control: AbstractControl,
  ): { [key: string]: any } | null => {
    const val = control.value;
    if (val === null || val === undefined || val === '') return null;
    const str = String(val);
    const dotIndex = str.indexOf('.');
    if (dotIndex !== -1 && str.length - dotIndex - 1 > 2) {
      return { tooManyDecimals: true };
    }
    return null;
  };

  // MAX > MIN VALIDATOR
  maxGreaterThanMinValidator() {
    return (group: AbstractControl): { [key: string]: any } | null => {
      const minVal = group.get('min')?.value;
      const maxVal = group.get('max')?.value;
      if (
        minVal !== null &&
        minVal !== '' &&
        maxVal !== null &&
        maxVal !== ''
      ) {
        if (Number(maxVal) <= Number(minVal)) {
          return { maxNotGreaterThanMin: true };
        }
      }
      return null;
    };
  }

  // GAPLESS VALIDATOR
  gaplessRangeValidator = (): any => {
    return (control: FormArray) => {
      if (!control || !control.controls || control.controls.length === 0)
        return null;

      // Debug log
      // console.log('Validating ranges:', control.value);

      // 1. First range MUST start at 0
      const first = control.at(0).value;
      if (first && Number(first.min) !== 0) {
        return { firstRangeZeroError: true };
      }

      // 2. Continuous Check
      if (control.controls.length > 1) {
        let hasGap = false;
        for (let i = 1; i < control.controls.length; i++) {
          const prev = control.controls[i - 1].value;
          const curr = control.controls[i].value;

          // Continuous Check (e.g. 0-10, 11-20 or 0-50, 50.01-60)
          // Use a small epsilon to handle JavaScript floating point precision issues
          const diff = Number(curr.min) - Number(prev.max);
          const isGapless =
            Math.abs(diff - 1) < 0.0001 ||
            Math.abs(diff - 0.01) < 0.0001 ||
            Math.abs(diff) < 0.0001;

          if (!isGapless) {
            hasGap = true;
            break;
          }
        }
        return hasGap ? { gapError: true } : null;
      }

      return null;
    };
  };

  resetSelection() {
    this.isCategorySelected = false;
    this.selectedCategory = null;
    this.dynamicFields = [];
    this.instantQuotesField = null;
    this.instantQuotesEnabled = false;
    this.clearCategoryTerms();
  }

  capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  trackByField(index: number, field: any): any {
    return field.id;
  }

  isFieldInvalid(code: string, isGroup: boolean = false): boolean {
    const control = this.configForm.get(code);
    if (!control) return false;

    // For groups (like weight ranges or checkbox inputs), check if the group itself is invalid
    // or if we should show error based on touch state.
    // Usually for groups we want to show error if any child is invalid and touched,
    // or if the group has a specific error like gapError.

    if (isGroup) {
      // Check if touched + invalid, or if gapError exists (which is on the array itself)
      return (
        (control.invalid && control.touched) || control.hasError('gapError')
      );
    }

    return control.invalid && control.touched;
  }

  hasAnyMinMaxError(code: string): boolean {
    const array = this.getWeightRanges(code);
    if (!array) return false;
    return array.controls.some(
      (group) => group.hasError('maxNotGreaterThanMin') && group.touched,
    );
  }

  isRangeError(code: string, index: number): boolean {
    const array = this.getWeightRanges(code);
    if (!array) return false;

    const group = array.at(index);
    if (!group) return false;

    if (group.invalid && group.touched) return true;

    if (index > 0 && array.touched) {
      const prev = array.at(index - 1).value;
      const curr = group.value;
      const diff = Number(curr.min) - Number(prev.max);
      const isGapless =
        Math.abs(diff - 1) < 0.0001 ||
        Math.abs(diff - 0.01) < 0.0001 ||
        Math.abs(diff) < 0.0001;
      if (!isGapless) {
        return true;
      }
    }

    return false;
  }

  formatKey(key: string): string {
    if (key === 'base_km') return 'Base KM';
    if (key === 'step_km') return 'Step KM';
    if (key === 'reject_max_weight_kg') return 'Reject Max Weight (kg)';
    if (key === 'reject_max_length_m') return 'Reject Max Length (m)';
    if (key === 'reject_max_width_m') return 'Reject Max Width (m)';
    if (key === 'reject_max_height_m') return 'Reject Max Height (m)';
    if (key === 'auto_trigger_weight_kg') return 'Auto-trigger Weight (kg)';
    if (key === 'residential_service_auto_trigger_length_m')
      return 'Residential Service Auto-trigger Length (m)';
    return key
      .split('_')
      .map((word) => this.capitalize(word))
      .join(' ');
  }

  isNumber(field: any, key: string): boolean {
    const objValue = field.value || field.value_pattern || {};
    return typeof objValue[key] === 'number';
  }

  shouldShowRateInput(field: any) {
    if (
      field.display_type !== 'weight_range_list' &&
      field.display_type !== 'surcharge_price'
    )
      return false;
    const code = field.attribute_code || '';
    const label = (field.attribute_label || '').toLowerCase();
    return (
      code.includes('bracket') ||
      code.includes('surcharge') ||
      label.includes('bracket') ||
      label.includes('surcharge') ||
      label.includes('fee')
    );
  }

  trackBySectionTitle(_index: number, section: TermsSection): string {
    return section.section_title;
  }

  get hasQuoteTermsContent(): boolean {
    return (
      this.viewGlobalSections.length > 0 || this.viewCategorySections.length > 0
    );
  }

  onActionButtonClick(field: any) {
    if (field.attribute_code === 'quote_terms_action') {
      this.openQuoteTermsModal();
    }
  }

  openQuoteTermsModal() {
    if (!this.selectedCategory?.value) return;

    this.modalService.open(this.quoteTermsModalTemplate, {
      centered: true,
      size: 'lg',
      scrollable: true,
    });
  }

  openIndependentTermModal(section: TermsSection) {
    this.selectedIndependentSection = section;
    this.modalService.open(this.independentTermModalTemplate, {
      centered: true,
      size: 'lg',
      scrollable: true,
    });
  }

  private clearCategoryTerms() {
    this.viewGlobalSections = [];
    this.viewCategorySections = [];
    this.independentCategorySections = [];
    this.selectedIndependentSection = null;
  }

  private applyCategoryTermsFull(
    full: {
      global?: { sections?: TermsSection[] } | null;
      category?: { sections?: TermsSection[] } | null;
    } | null,
  ) {
    const categorySections = full?.category?.sections ?? [];
    this.viewGlobalSections = [...(full?.global?.sections ?? [])];
    this.viewCategorySections = getCombinedCategorySections(categorySections);
    this.independentCategorySections =
      getIndependentCategorySections(categorySections);
  }

  private async loadCategoryTerms(categoryId: string) {
    this.isCategoryTermsLoading = true;
    this.clearCategoryTerms();

    try {
      const full =
        await this.appliedTermsService.getCategoryTermsFull(categoryId);
      this.applyCategoryTermsFull(full);
    } catch (err: any) {
      console.error('Error loading category terms:', err);
    } finally {
      this.isCategoryTermsLoading = false;
      this.cdr.detectChanges();
    }
  }
}
